/**
 * PointerControls — unified pointer input for mouse AND touch.
 *
 * Uses Pointer Events (not mouse/touch separately) so a finger drag and a mouse
 * drag are the same code path — essential for the mobile-browser target.
 * Supports: drag-to-pan, pinch-to-zoom, and tap (world-space) callbacks.
 */
import type { RenderRoot } from "@/render/RenderRoot";

export interface PointerCallbacks {
  onTapWorld?: (x: number, y: number) => void;
  /** Mouse hover (no button pressed) at client coords — for tooltips. */
  onHover?: (clientX: number, clientY: number) => void;
  /** Hover ended (press started or pointer left the canvas). */
  onHoverEnd?: () => void;
}

export class PointerControls {
  private el: HTMLElement;
  private render: RenderRoot;
  private cbs: PointerCallbacks;

  private pointers = new Map<number, { x: number; y: number }>();
  private lastPan: { x: number; y: number } | null = null;
  private pinchDist = 0;
  private downAt = 0;
  private moved = false;

  constructor(el: HTMLElement, render: RenderRoot, cbs: PointerCallbacks = {}) {
    this.el = el;
    this.render = render;
    this.cbs = cbs;
    el.addEventListener("pointerdown", this.onDown);
    el.addEventListener("pointermove", this.onMove);
    el.addEventListener("pointerup", this.onUp);
    el.addEventListener("pointercancel", this.onUp);
    el.addEventListener("pointerleave", this.onLeave);
    el.addEventListener("wheel", this.onWheel, { passive: false });
  }

  private onDown = (e: PointerEvent): void => {
    this.cbs.onHoverEnd?.(); // hide the hover tooltip while pressing / dragging
    this.el.setPointerCapture?.(e.pointerId);
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    this.downAt = performance.now();
    this.moved = false;
    if (this.pointers.size === 1) this.lastPan = { x: e.clientX, y: e.clientY };
    if (this.pointers.size === 2) this.pinchDist = this.currentPinch();
  };

  private onMove = (e: PointerEvent): void => {
    if (!this.pointers.has(e.pointerId)) {
      this.cbs.onHover?.(e.clientX, e.clientY); // hovering (no button pressed)
      return;
    }
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (this.pointers.size === 2) {
      // pinch zoom
      const d = this.currentPinch();
      if (this.pinchDist > 0) {
        this.render.zoomBy(d / this.pinchDist);
      }
      this.pinchDist = d;
      this.moved = true;
      return;
    }

    if (this.lastPan) {
      const dx = e.clientX - this.lastPan.x;
      const dy = e.clientY - this.lastPan.y;
      if (Math.abs(dx) + Math.abs(dy) > 3) this.moved = true;
      this.render.camera.x -= dx / this.render.camera.zoom;
      this.render.camera.y -= dy / this.render.camera.zoom;
      this.lastPan = { x: e.clientX, y: e.clientY };
    }
  };

  private onUp = (e: PointerEvent): void => {
    const wasTap = !this.moved && performance.now() - this.downAt < 250;
    if (wasTap && this.cbs.onTapWorld) {
      const w = this.render.screenToWorld(e.clientX, e.clientY);
      this.cbs.onTapWorld(w.x, w.y);
    }
    this.pointers.delete(e.pointerId);
    this.lastPan = null;
    if (this.pointers.size < 2) this.pinchDist = 0;
  };

  private onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    this.render.zoomBy(Math.exp(-e.deltaY * 0.001));
  };

  private onLeave = (): void => {
    this.cbs.onHoverEnd?.();
  };

  private currentPinch(): number {
    const pts = [...this.pointers.values()];
    if (pts.length < 2) return 0;
    return Math.hypot(pts[0]!.x - pts[1]!.x, pts[0]!.y - pts[1]!.y);
  }

  destroy(): void {
    this.el.removeEventListener("pointerdown", this.onDown);
    this.el.removeEventListener("pointermove", this.onMove);
    this.el.removeEventListener("pointerup", this.onUp);
    this.el.removeEventListener("pointercancel", this.onUp);
    this.el.removeEventListener("pointerleave", this.onLeave);
    this.el.removeEventListener("wheel", this.onWheel);
  }
}
